import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import HeroSection from '../components/HeroSection';
import Article from '../components/Article';
import {
  SEO,
  Content,
  Text,
  Spacing,
  devices,
  Stack,
  WhiteButton,
  BlueButtonBasic,
} from '../components/common';
import Modal from '../components/Modal';
import { ExternalLink } from '../components/SVG';
import Markdown from '../components/Markdown';
import Accordion from '../components/Accordion';
import { NonFinnishService, Employee } from '../@types/types';
import theme from '../theme';
import { RichTextContent } from '../components/StyledComponents';

type Props = {
  data: { page: NonFinnishService; arrow: { publicURL: string } };
  location: Location;
};

const EnglishServices = ({ data, location }: Props) => {
  const {
    page: {
      MetaData,
      PageTopSection,
      employees,
      contentTitle,
      accordionContent,
      Content: ServiceContent,
      accordion,
      EmployeeSectionTitle,
    },
    arrow,
  } = data;

  const [showModal, setShowModal] = useState(false);
  const [employeeData, setEmployeeData] = useState(false);
  const [randomEmployeeData, sestRandomEmployeeData] = useState<Employee[]>([]);

  const openModal = (employee: any) => {
    setShowModal((showModal) => !showModal);
    setEmployeeData(employee);
  };

  const lastItem: number = ServiceContent.length + 1;

  const shuffleEmployee = (array: any[]) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  useEffect(() => {
    sestRandomEmployeeData(shuffleEmployee(employees));
  }, []);

  return (
    <>
      <SEO
        pathname={location.pathname}
        title={MetaData.MetaTitle}
        description={MetaData.MetaDescription}
        keywords={MetaData.metaKeywords}
      />

      <Modal
        showModal={showModal}
        setShowModal={setShowModal}
        employeeData={employeeData}
      />

      <HeroSection topSection={PageTopSection} />

      <Article
        title={contentTitle}
        content={ServiceContent}
        backgroundColor={theme.colors.mutedLightBlue}
      />

      <AccordionContainer>
        <ArrowContainer>
          <Section>
            <Content>
              <RichTextContent>{accordionContent}</RichTextContent>
            </Content>
          </Section>
          <ArrowImage src={arrow.publicURL} alt="picture of an arrow" />
          <Section>
            {accordion.map((item, index) => (
              <Accordion key={index} header={item.Header} text={item.Content} />
            ))}
          </Section>
        </ArrowContainer>
      </AccordionContainer>

      {randomEmployeeData.length !== 0 ? (
        <Container bgColor={'white' || 'lightBlue'}>
          <>
            <Content>
              <EmployeeTitle variant="title-2">
                {EmployeeSectionTitle || 'Our specialists'}
              </EmployeeTitle>
              <Spacing axis="y" amount="xxlarge" />
              <EmployeeContainer>
                {employees.map((employee: Employee, index: number) => (
                  <ProfileContainer key={index}>
                    <ContentContainer axis="y" spacing="medium" align="center">
                      <Image src={employee.Picture?.publicURL} />
                      <ProfileDetails>
                        <NameAndTitle>
                          <NameText variant={'subtitle'}>
                            {employee.Name}
                          </NameText>
                          <TitleText variant={'body'}>
                            {employee.Title}
                          </TitleText>
                        </NameAndTitle>

                        {employee.Description && (
                          <DescriptionSection>
                            {employee.Description}
                          </DescriptionSection>
                        )}

                        <ExternalLinks>
                          {employee.ReservationLink && (
                            <Button href={employee.ReservationLink}>
                              Varaa aika
                              <CustomIcon fill={'#fff'} />
                            </Button>
                          )}

                          <ModalButton
                            onClick={() => openModal(employee)}
                            linkExist={employee.ReservationLink}
                          >
                            Lisätietoa
                          </ModalButton>
                        </ExternalLinks>
                      </ProfileDetails>
                    </ContentContainer>
                  </ProfileContainer>
                ))}
              </EmployeeContainer>
            </Content>
          </>
        </Container>
      ) : null}
    </>
  );
};

export default EnglishServices;

export const englishServiceQuery = graphql`
  query EnglishServicePageQuery {
    page: strapiPalvelutEnglanniksi {
      ...englishServicesFragment
    }
    arrow: file(name: { eq: "blue-arrow" }) {
      publicURL
    }
  }
`;

const Container = styled.div<{ bgColor: string }>`
  background-color: ${(props: any) => props.theme.colors[props.bgColor]};
  padding: 100px 0px;
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 30%;
  align-items: center;
  padding-bottom: 80px;

  @media ${devices.tablet} {
    flex: 0 0 50%;

    &:last-child {
      padding-bottom: 0px;
    }
  }
`;

const ContentContainer = styled(Stack)`
  width: 300px;

  @media ${devices.tablet} {
    width: 100%;
  }
`;

const Image = styled.img`
  border-radius: 50%;
  height: 300px;
  width: 300px;
  margin: auto;

  @media ${devices.mobileS} {
    height: 200px;
    width: 200px;
  }
`;

const ProfileDetails = styled(Stack)`
  display: flex;
  flex-direction: column;
  margin-top: ${(props) => props.theme.spacing.medium};
  text-align: center;

  @media ${devices.mobileXL} {
    margin: auto;
  }
`;

const NameText = styled(Text)`
  font-size: ${(props) => props.theme.sizing.medium};

  @media ${devices.tablet} {
    text-align: center;
  }
`;

const TitleText = styled(Text)`
  font-style: italic;
  font-size: calc((${(props) => props.theme.sizing.default}) + 1px);

  @media ${devices.tablet} {
    text-align: center;
  }
`;

const DescriptionSection = styled(Markdown)`
  line-height: 20px;

  @media ${devices.tablet} {
    text-align: center;
  }
`;

const ExternalLinks = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media ${devices.tablet} {
    flex-direction: column;
  }
`;

const CustomIcon = styled(ExternalLink)`
  margin-left: 10px;
`;
const NameAndTitle = styled.div`
  @media ${devices.mobileXL} {
    margin-top: ${(props) => props.theme.sizing.default};
  }
`;

const Button = styled(BlueButtonBasic)`
  @media ${devices.tablet} {
    width: 80%;
    max-width: 300px;
    margin: ${(props) => props.theme.sizing.default} 0px;
    justify-content: space-between;
  }
`;

const ModalButton = styled(WhiteButton)<{ linkExist: string }>`
  cursor: pointer;
  margin: ${(props) => !props.linkExist && 'auto'};
  width: max-content;
`;

const EmployeeContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 20px;

  @media ${devices.laptopXS} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${devices.mobileXL} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const EmployeeTitle = styled(Text)`
  @media ${devices.mobileXL} {
    text-align: center;
  }
`;

const Section = styled.div`
  padding: ${(props) => props.theme.spacing.medium} 0px;
  width: 950px;
  margin-left: auto;
  margin-right: auto;

  @media ${devices.laptop} {
    padding: ${(props) => props.theme.spacing.medium} 0px;
    width: 650px;
  }

  @media ${devices.mobileXL} {
    width: 90% !important;
  }
`;

const AccordionContainer = styled.div`
  background-color: ${(props) => props.theme.colors.lightBlue};
  padding-bottom: ${(props) => props.theme.spacing.xxxlarge};
  position: relative;
`;

const ArrowImage = styled.img`
  width: 120px;
  height: 160px;
  position: absolute;
  left: 0px;
  top: 115px;

  @media ${devices.laptopL} {
    display: none;
  }
`;

const ArrowContainer = styled.div`
  width: 85%;
  max-width: 1300px;
  margin: auto;
  position: relative;

  @media ${devices.laptopL} {
    width: 100%;
  }
`;
